import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import Icon from "@/components/ui/Icon.jsx";
import { SearchInput, Select } from "@/components/ui/Form.jsx";
import { Avatar, Callout, EmptyState, StatCard } from "@/components/ui/Display.jsx";
import Pagination, { PaginationInfo } from "@/components/ui/Pagination.jsx";
import { SortableTh, TableWrap, Td, Th, Tr } from "@/components/ui/Table.jsx";
import Tag, { RoleTag, VersionTag } from "@/components/ui/Tag.jsx";
import { useConfirm, useToast } from "@/components/ui/feedback.js";
import UserFormDrawer from "@/components/users/UserFormDrawer.jsx";

import { teamService, userService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { useAuth } from "@/context/auth.js";
import { formatLastActive } from "@/lib/format.js";
import { nextSort, sortRows } from "@/lib/sort.js";
import { latestDesktopVersionQuery } from "@/lib/queries.js";
import { VERSION_TIERS } from "@/lib/tokens.js";
import {
    EMPTY_FILTERS,
    USER_SORTS,
    appVersionOf,
    filterUsers,
    osOf,
    osOptions,
    teamIdOf,
    teamNameOf,
    tierOf,
    userStats,
} from "@/lib/users.js";

/*
 * Replaces frontend/app/(dashboard)/users/page.js + UserSearchFilter.js.
 *
 * This is the CRUD template every later page copies: query → filter bar →
 * table → drawer form → confirm → mutate → invalidate. No optimistic updates
 * anywhere; the server is the source of truth and a refetch is cheap. An
 * optimistic row that a validation error then removes is worse than a half
 * second of waiting.
 */

// A stable empty array: `query.data ?? []` allocates a new one every render,
// which changes the deps of every useMemo below it and defeats them entirely.
const NONE = [];

export default function Users() {
    const [params, setParams] = useSearchParams();
    const queryClient = useQueryClient();
    const toast = useToast();
    const confirm = useConfirm();
    const { user: me, can } = useAuth();

    // The team filter is URL-driven so the Teams page can link straight to it.
    const [filters, setFilters] = useState(() => ({
        ...EMPTY_FILTERS,
        team: params.get("team") || "",
    }));
    const [sort, setSort] = useState({ by: "lastActive", dir: "desc" });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const [editing, setEditing] = useState(null); // null = closed, {} = create
    const [formError, setFormError] = useState("");

    // `?? []` is not defensive padding: TanStack Query v5 REJECTS a query that
    // resolves to undefined, so a response missing the key would surface as a
    // thrown "Query data cannot be undefined" rather than an empty table.
    const usersQuery = useQuery({
        queryKey: ["users"],
        queryFn: () => userService.getAllUsers().then((r) => r.data?.data?.users ?? []),
    });

    const teamsQuery = useQuery({
        queryKey: ["teams"],
        queryFn: () => teamService.getOrganizationTeams().then((r) => r.data?.data?.teams ?? []),
    });

    const users = usersQuery.data ?? NONE;
    const teams = teamsQuery.data ?? NONE;

    // Latest published desktop version (catalog-driven, not a hardcoded constant).
    const { data: latestDesktop } = useQuery(latestDesktopVersionQuery());

    const stats = useMemo(() => userStats(users, latestDesktop), [users, latestDesktop]);

    const rows = useMemo(() => {
        const matched = filterUsers(users, filters, latestDesktop);
        return sortRows(matched, sort, USER_SORTS);
    }, [users, filters, sort, latestDesktop]);

    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const current = Math.min(page, totalPages);
    const visible = rows.slice((current - 1) * pageSize, current * pageSize);

    const setFilter = (key, value) => {
        setFilters((f) => ({ ...f, [key]: value }));
        setPage(1);
        if (key === "team") {
            // Keep the URL honest so the view is linkable and survives a reload.
            const next = new URLSearchParams(params);
            if (value) next.set("team", value);
            else next.delete("team");
            setParams(next, { replace: true });
        }
    };

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ["users"] });

    const saveUser = useMutation({
        mutationFn: (values) =>
            editing?._id
                ? userService.updateUser(editing._id, values)
                : userService.createUser(values),
        onSuccess: (_res, values) => {
            toast(`${values.name} ${editing?._id ? "updated" : "created"}`);
            setEditing(null);
            setFormError("");
            invalidate();
        },
        onError: (error) =>
            setFormError(apiErrorMessage(error, "Could not save this user. Please try again.")),
    });

    const setActive = useMutation({
        mutationFn: ({ user, isActive }) => userService.updateUser(user._id, { isActive }),
        onSuccess: (_res, { user, isActive }) => {
            toast(`${user.name} ${isActive ? "activated" : "deactivated"}`);
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not change the status."), "error"),
    });

    const removeUser = useMutation({
        mutationFn: (user) => userService.deleteUser(user._id),
        onSuccess: (_res, user) => {
            toast(`${user.name} deleted`, "error");
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not delete this user."), "error"),
    });

    /*
     * You cannot deactivate or delete yourself.
     *
     * The backend does not stop you: updateUser lets an admin patch their own
     * isActive, and deleting your own account would take your organization's
     * only admin with it. Guarding in the UI is not security — it is stopping
     * someone locking themselves out with two clicks.
     */
    const isSelf = (user) => user._id === me?.id || user._id === me?._id;

    const askToggle = async (user) => {
        const deactivating = user.isActive;
        const ok = await confirm({
            tone: deactivating ? "warning" : "success",
            title: deactivating ? `Deactivate ${user.name}?` : `Activate ${user.name}?`,
            body: deactivating
                ? "They cannot sign in or use the desktop app until reactivated. The desktop client signs out on its next settings sync — usually within a minute."
                : "This user can sign in and use the desktop app again.",
            okLabel: deactivating ? "Deactivate" : "Activate",
        });
        if (ok) setActive.mutate({ user, isActive: !user.isActive });
    };

    const askDelete = async (user) => {
        const ok = await confirm({
            tone: "danger",
            title: "Delete this user?",
            body: (
                <>
                    <p>This permanently deletes:</p>
                    <ul className="mt-1.5 ml-4 list-disc space-y-0.5">
                        <li>
                            The account{" "}
                            <strong className="font-semibold text-slate-700 dark:text-slate-200">
                                {user.name}
                            </strong>
                        </li>
                        <li>All time entries and idle records</li>
                        <li>All screenshots, including the stored image files</li>
                        <li>All activity and app-usage data</li>
                        <li>Their system information</li>
                    </ul>
                    <p className="mt-2 font-semibold text-rose-600 dark:text-rose-400">
                        This cannot be undone.
                    </p>
                </>
            ),
            okLabel: "Yes, delete user",
        });
        if (ok) removeUser.mutate(user);
    };

    const activeFilters = Object.entries(filters).filter(
        ([key, value]) => value && value !== EMPTY_FILTERS[key],
    );

    return (
        <>
            <PageChrome breadcrumb={["Users"]} title="Users">
                {can("manageUsers") && (
                    <Button variant="primary" onClick={() => setEditing({})} data-add-user>
                        <Icon name="user" className="size-4" />
                        Add user
                    </Button>
                )}
            </PageChrome>

            <main className="space-y-5 p-4 sm:p-5">
                <div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Users</h1>
                    <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                        Everyone in your organization, and the desktop build each one is running.
                    </p>
                </div>

                {usersQuery.isError && (
                    <Callout tone="danger">
                        {apiErrorMessage(usersQuery.error, "Failed to load users.")}{" "}
                        <button
                            type="button"
                            onClick={() => usersQuery.refetch()}
                            className="font-semibold underline"
                        >
                            Try again
                        </button>
                    </Callout>
                )}

                {/* ---- Stats. Counted over everyone, not the filtered view ---- */}
                <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard
                        tone="brand"
                        label="Total users"
                        value={stats.total}
                        hint="In this organization"
                        icon={<Icon name="team" className="size-4" />}
                    />
                    <StatCard
                        tone="emerald"
                        label="Active"
                        value={stats.active}
                        hint="Can sign in and track"
                        icon={<Icon name="user" className="size-4" />}
                        onClick={() => setFilter("status", "active")}
                        active={filters.status === "active"}
                    />
                    <StatCard
                        tone="slate"
                        label="Never installed"
                        value={stats.neverInstalled}
                        hint="No desktop app has checked in"
                        icon={<Icon name="squares" className="size-4" />}
                        onClick={() => setFilter("version", "none")}
                        active={filters.version === "none"}
                    />
                    <StatCard
                        tone="rose"
                        label="Outdated app"
                        value={stats.outdated}
                        hint="Pre-2.0 build — predates the data-stall fixes"
                        icon={<Icon name="shield" className="size-4" />}
                        onClick={() => setFilter("version", "stale")}
                        active={filters.version === "stale"}
                    />
                </section>

                <Card>
                    <CardHead>
                        <CardTitle>All users</CardTitle>
                    </CardHead>

                    <CardBody>
                        {/* ---- Filter bar ---- */}
                        <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
                            <SearchInput
                                value={filters.search}
                                onChange={(e) => setFilter("search", e.target.value)}
                                placeholder="Search name, email or team…"
                                aria-label="Search users"
                                className="w-full sm:w-72"
                            />

                            <Select
                                size="sm"
                                value={filters.role}
                                onChange={(e) => setFilter("role", e.target.value)}
                                aria-label="Filter by role"
                                className="w-auto"
                            >
                                <option value="">All roles</option>
                                <option value="admin">Admin</option>
                                <option value="manager">Manager</option>
                                <option value="employee">Employee</option>
                            </Select>

                            <Select
                                size="sm"
                                value={filters.status}
                                onChange={(e) => setFilter("status", e.target.value)}
                                aria-label="Filter by status"
                                className="w-auto"
                            >
                                <option value="">Any status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </Select>

                            <Select
                                size="sm"
                                value={filters.team}
                                onChange={(e) => setFilter("team", e.target.value)}
                                aria-label="Filter by team"
                                className="w-auto"
                            >
                                <option value="">All teams</option>
                                {teams.map((team) => (
                                    <option key={team._id} value={team._id}>
                                        {team.name}
                                    </option>
                                ))}
                            </Select>

                            <Select
                                size="sm"
                                value={filters.os}
                                onChange={(e) => setFilter("os", e.target.value)}
                                aria-label="Filter by operating system"
                                className="w-auto"
                            >
                                <option value="">Any OS</option>
                                {osOptions(users).map((os) => (
                                    <option key={os} value={os}>
                                        {os}
                                    </option>
                                ))}
                            </Select>

                            <Select
                                size="sm"
                                value={filters.version}
                                onChange={(e) => setFilter("version", e.target.value)}
                                aria-label="Filter by app version"
                                className="w-auto"
                            >
                                <option value="">Any app version</option>
                                {Object.entries(VERSION_TIERS).map(([key, tier]) => (
                                    <option key={key} value={key}>
                                        {tier.label}
                                    </option>
                                ))}
                            </Select>

                            {activeFilters.length > 0 && (
                                <Button
                                    variant="link"
                                    onClick={() => {
                                        setFilters(EMPTY_FILTERS);
                                        setPage(1);
                                        const next = new URLSearchParams(params);
                                        next.delete("team");
                                        setParams(next, { replace: true });
                                    }}
                                    data-clear-filters
                                >
                                    Clear filters
                                </Button>
                            )}
                        </div>

                        {/* ---- Table ---- */}
                        <TableWrap minWidth="1000px">
                            <thead>
                                <tr>
                                    <SortableTh field="name" sort={sort} onSort={(f) => setSort((s) => nextSort(s, f))}>
                                        User
                                    </SortableTh>
                                    <SortableTh field="role" sort={sort} onSort={(f) => setSort((s) => nextSort(s, f))} className="w-32">
                                        Role
                                    </SortableTh>
                                    <SortableTh field="team" sort={sort} onSort={(f) => setSort((s) => nextSort(s, f))} className="w-40">
                                        Team
                                    </SortableTh>
                                    <SortableTh field="os" sort={sort} onSort={(f) => setSort((s) => nextSort(s, f))} className="w-32">
                                        OS
                                    </SortableTh>
                                    <SortableTh field="version" sort={sort} onSort={(f) => setSort((s) => nextSort(s, f))} className="w-40">
                                        App version
                                    </SortableTh>
                                    <SortableTh
                                        field="lastActive"
                                        sort={sort}
                                        onSort={(f) => setSort((s) => nextSort(s, f, "desc"))}
                                        className="w-36"
                                    >
                                        Last active
                                    </SortableTh>
                                    <Th className="w-28">Status</Th>
                                    <Th last className="w-32 text-right">
                                        Actions
                                    </Th>
                                </tr>
                            </thead>

                            <tbody>
                                {visible.map((user) => (
                                    <Tr key={user._id}>
                                        <Td>
                                            <Link
                                                to={`/dashboard/${user._id}`}
                                                className="flex min-w-0 items-center gap-2.5 hover:text-brand-600 dark:hover:text-brand-400"
                                            >
                                                <Avatar name={user.name} size="sm" />
                                                <span className="min-w-0">
                                                    <span className="flex items-center gap-1.5">
                                                        <span className="truncate font-medium">
                                                            {user.name}
                                                        </span>
                                                        {isSelf(user) && <Tag tone="slate">You</Tag>}
                                                    </span>
                                                    <span className="block truncate text-[11px] text-slate-400 dark:text-slate-500">
                                                        {user.email}
                                                    </span>
                                                </span>
                                            </Link>
                                        </Td>

                                        <Td>
                                            <RoleTag role={user.role} />
                                        </Td>

                                        <Td>
                                            {teamNameOf(user) ? (
                                                <Link
                                                    to={`/users?team=${teamIdOf(user)}`}
                                                    className="hover:text-brand-600 dark:hover:text-brand-400"
                                                >
                                                    {teamNameOf(user)}
                                                </Link>
                                            ) : (
                                                <span className="text-slate-400 dark:text-slate-500">
                                                    Unassigned
                                                </span>
                                            )}
                                        </Td>

                                        <Td className="text-slate-500 dark:text-slate-400">
                                            {osOf(user) || "—"}
                                        </Td>

                                        <Td>
                                            <span className="flex items-center gap-2">
                                                {appVersionOf(user) ? (
                                                    <span className="font-mono text-[12.5px] font-semibold">
                                                        v{appVersionOf(user)}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-400 dark:text-slate-500">
                                                        —
                                                    </span>
                                                )}
                                                <VersionTag tier={tierOf(user, latestDesktop)} />
                                            </span>
                                        </Td>

                                        <Td className="text-slate-500 dark:text-slate-400">
                                            {formatLastActive(user.lastActive)}
                                        </Td>

                                        <Td>
                                            <Tag tone={user.isActive ? "emerald" : "rose"}>
                                                {user.isActive ? "Active" : "Inactive"}
                                            </Tag>
                                        </Td>

                                        <Td last className="text-right">
                                            {can("manageUsers") && (
                                                <span className="flex items-center justify-end gap-0.5">
                                                    <Button
                                                        variant="icon"
                                                        aria-label={`Edit ${user.name}`}
                                                        onClick={() => {
                                                            setFormError("");
                                                            setEditing(user);
                                                        }}
                                                        data-edit={user._id}
                                                    >
                                                        <Icon name="cog" className="size-4" />
                                                    </Button>

                                                    <Button
                                                        variant="icon"
                                                        aria-label={`${user.isActive ? "Deactivate" : "Activate"} ${user.name}`}
                                                        disabled={isSelf(user)}
                                                        title={
                                                            isSelf(user)
                                                                ? "You cannot change your own account status"
                                                                : undefined
                                                        }
                                                        onClick={() => askToggle(user)}
                                                        data-toggle={user._id}
                                                    >
                                                        <Icon name="shield" className="size-4" />
                                                    </Button>

                                                    <Button
                                                        variant="icon"
                                                        aria-label={`Delete ${user.name}`}
                                                        disabled={isSelf(user)}
                                                        title={
                                                            isSelf(user)
                                                                ? "You cannot delete your own account"
                                                                : undefined
                                                        }
                                                        onClick={() => askDelete(user)}
                                                        data-delete={user._id}
                                                        className="text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
                                                    >
                                                        <Icon name="logout" className="size-4" />
                                                    </Button>
                                                </span>
                                            )}
                                        </Td>
                                    </Tr>
                                ))}

                                {!visible.length && (
                                    <tr>
                                        <td colSpan={8}>
                                            <EmptyState
                                                title={
                                                    users.length
                                                        ? "No user matches these filters"
                                                        : "No users yet"
                                                }
                                                hint={
                                                    users.length
                                                        ? "Try clearing the version or team filter."
                                                        : "Add your first user to get started."
                                                }
                                                action={
                                                    users.length ? (
                                                        <Button
                                                            onClick={() => {
                                                                setFilters(EMPTY_FILTERS);
                                                                setPage(1);
                                                            }}
                                                        >
                                                            Clear filters
                                                        </Button>
                                                    ) : null
                                                }
                                            />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </TableWrap>

                        <div className="mt-3.5 flex flex-wrap items-center gap-3">
                            <PaginationInfo
                                page={current}
                                pageSize={pageSize}
                                total={rows.length}
                                noun="users"
                                sizes={[20, 50, 100]}
                                onPageSizeChange={(n) => {
                                    setPageSize(n);
                                    setPage(1);
                                }}
                            />
                            <div className="ml-auto">
                                <Pagination page={current} totalPages={totalPages} onChange={setPage} />
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </main>

            <UserFormDrawer
                open={Boolean(editing)}
                user={editing?._id ? editing : null}
                onClose={() => {
                    setEditing(null);
                    setFormError("");
                }}
                onSubmit={(values) => saveUser.mutate(values)}
                saving={saveUser.isPending}
                error={formError}
            />
        </>
    );
}
